import { useQuery, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import api from 'services';
import { ApiError } from 'services/api.errors';
import { PortfolioTransactionsType } from 'services/requests';
import { useQueryParams } from 'hooks/routes';
import { formatInputDateForBE } from 'utils';
import { QueryKeys } from 'constants/queryKeys';
import { Parameters } from 'constants/queryParameters';

export const useGetProfileActiveLimitOrders = (enableToFetch?: boolean) => {
  const queryClient = useQueryClient();

  const startDateString = formatInputDateForBE(new Date());
  const endDateString = formatInputDateForBE(new Date());
  const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const profileId =
    useQueryParams(Parameters.iraUserId) ??
    useQueryParams(Parameters.independentIraUserId);

  const queryKey = [QueryKeys.activeLimitOrders, profileId];

  const queryFn =
    (profileId: number | undefined) =>
    async (): Promise<{ limitOrdersLength: number | undefined }> => {
      const [, cachedData] = queryClient
        .getQueriesData<PortfolioTransactionsType>({
          queryKey: [QueryKeys.profileTransactions, profileId?.toString()],
        })
        .find(([, data]) => !!data) ?? [null, undefined];

      if (typeof cachedData?.limitOrders === 'number') {
        return { limitOrdersLength: cachedData?.limitOrders };
      }

      const { data } = await api.getProfileTransactions(
        profileId,
        startDateString,
        endDateString,
        timeZoneId,
      );

      return { limitOrdersLength: data.limitOrders?.length ?? undefined };
    };

  return useQuery<{ limitOrdersLength: number | undefined }, AxiosError<ApiError>>(
    queryKey,
    queryFn(Number(profileId)),
    {
      enabled: !!profileId && enableToFetch,
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};
